import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { IObject } from 'src/app/models/+global';
import { IConfigManager, IConfigResolve } from 'src/app/models/config-manager';
import { Mutex } from 'src/app/shared/Mutex';
import { ConfigsEndpointService } from './configs-endpoint-service';

@Injectable({
    providedIn: 'root'
})
export class ConfigsService {
    private collectionMutex = new Mutex();
    private medicaidGroups: { time: Date, data: IConfigManager[] };

    constructor(private endpointService: ConfigsEndpointService) { }

    private allConfigs: IConfigManager[];

    private editorConfigs: { key: string, expression: string }[] = [];

    public resolve(key: string, req: IObject): Promise<string[]> {
        return this.endpointService.resolve(key, req);
    }

    public firstOrDefault(key: string, req: IObject): Promise<IConfigResolve> {
        return this.endpointService.firstOrDefault(key, req);
    }

    public async getEditorConfig(key: string): Promise<string> {
        let returnConfig = this.editorConfigs.find(x => x.key == key);

        if (returnConfig == undefined) {
            let editorConfig = await this.endpointService.firstOrDefault("CONFIG_ENTRY", { Key: key });

            returnConfig = {
                key: key,
                expression: editorConfig?.value
            };

            this.editorConfigs.push(returnConfig);
        }

        return returnConfig.expression;
    }

    public getCachedEditorConfig(): IConfigManager[] {
        return this.allConfigs.filter(x => x.Key == 'CONFIG_ENTRY');
    }

    public getCachedNonEditorConfig(): IConfigManager[] {
        return this.allConfigs.filter(x => x.Key !== 'CONFIG_ENTRY');
    }

    public upsertEditorConfig(config: IConfigManager) {

        const predicateParts = config.Predicate.split("==");
        if (predicateParts.length == 2) {
            // Only try to parse if the predicate is in the correct format. Key == value, and only once.
            const predicateKey = predicateParts[1].trim().replace(/"/g, '');

            let updateConfig = this.editorConfigs.find(x => x.key == predicateKey);

            if (updateConfig != undefined) {
                updateConfig.expression = config.Expression;
            } else {
                this.editorConfigs.push({
                    key: predicateKey,
                    expression: config.Expression
                });
            }
        }
    }

    public async getAll(): Promise<any> {
        this.allConfigs = await this.endpointService.getall();
        return this.allConfigs;
    }

    public async getById(id: number): Promise<IConfigManager> {
        const configById = await this.endpointService.getById(id.toString());

        if (!this.allConfigs) {
            this.allConfigs = [];
        }

        // While we have the database copy, update the cache.
        let updateConfig = this.allConfigs.find(x => x.Id == id);
        if (updateConfig != undefined) {
            updateConfig.Expression = configById.Expression;
            updateConfig.Predicate = configById.Predicate;
            updateConfig.Metadata = configById.Metadata;
        }
        return configById;
    }

    public getKey(key: string): Promise<any> {
        let foundKey = null;
        if (this.allConfigs && this.allConfigs.length > 0) {
            foundKey = this.allConfigs.find(k => {
                k.Key === key
            });
        }
        return (foundKey) ? foundKey : this.endpointService.getKey(key);
    }

    public async getKeys(key: string): Promise<any[]> {
        return await this.endpointService.getKey(key);
    }

    public updateConfig(config: IConfigManager): Promise<any> {
        if (config.Key === "CONFIG_ENTRY") {
            this.upsertEditorConfig(config);
        }

        return this.endpointService.updateConfig(config);
    }

    public addConfig(config: any): Promise<any> {
        if (config.Key === "CONFIG_ENTRY") {
            // If this is a config entry, we need to add to the editorConfigs array, so the user experience is updated.
            this.upsertEditorConfig(config);
        }

        return this.endpointService.addConfig(config);
    }

    public deleteConfig(config: any): Promise<any> {
        return this.endpointService.deleteConfig(config);
    }

    async getCachedMedicaidGroups<T extends IConfigManager>(): Promise<T[]> {
        const unlock = await this.collectionMutex.lock();
        try {
            const expirationSeconds = 10; // 10 seconds
            const getFromCache = this.medicaidGroups && (this.medicaidGroups.time > moment().subtract(expirationSeconds, 'second').toDate());
            if (!getFromCache) {
                const response = await this.getKeys('MEDICAID_GROUP');
                this.medicaidGroups = { time: new Date(), data: response };;
            }

            return this.medicaidGroups.data as T[];

        } finally {
            unlock();
        }
    }

    async configurePredicateLookups(fields: any) {
        for (const key of Object.keys(fields)) {
            // Object.keys(fields).forEach(async key => {
            let field = fields[key];

            if (!(["number", "boolean", "string", "category", "date"].includes(field.type))) {
                const configPredicate = await this.firstOrDefault("PREDICATE_LOOKUP", { Key: field.type });
                field.type = "category";
                field.operators = ["=", "!="];

                let valueJson: any[];
                try {
                    valueJson = JSON.parse(configPredicate.value);
                } catch (e) {
                    console.error(`Error parsing PREDICATE_LOOKUP ${key}`, e);
                };

                if (Array.isArray(valueJson)) {
                    field.options = valueJson.map(x => { return { name: x.name, value: x.id } });
                } else {
                    field.options = [];
                }
            }
        }
    }

}
